'use client';

import { Box, Grid } from '@mui/material';
import { Suspense, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FeatureViewed } from '@repo/segment/components/FeatureViewed';
import { dynamicWithRetry } from '../../../../DynamicWithRetry';
import { CalculatorForm } from './CalculatorForm';
import {
  createHYSACalculatorStore,
  defaultFormFieldValues,
} from './CalculatorForm/calculatorStore';
import { CalculatorResultsGraphSkeleton } from './CalculatorResultsGraph/CalculatorResultsGraphSkeleton';
import { Results } from './Results';

const CalculatorResultsGraph = dynamicWithRetry<Record<string, never>>(
  () =>
    import('./CalculatorResultsGraph/index').then(mod => ({ default: mod.CalculatorResultsGraph })),
  { componentName: 'CalculatorResultsGraph' },
);

type CompoundCalculatorProps = {
  defaultApy: number;
};

export function CompoundCalculator({ defaultApy }: CompoundCalculatorProps) {
  useMemo(() => {
    createHYSACalculatorStore({
      ...defaultFormFieldValues,
      annualPercentageYield: defaultApy.toString(),
    });
  }, [defaultApy]);

  return (
    <FeatureViewed entityName="hysa_calculator" entitySection="banking">
      <Box
        data-testid="calculator-container"
        sx={{
          backgroundColor: 'white',
          borderRadius: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          padding: {
            xs: '1rem',
            md: '1.5rem',
          },
        }}
      >
        <CalculatorForm />
        <Grid
          container
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            alignItems: 'stretch',
            gap: '1.5rem',
          }}
        >
          <Grid size={{ xs: 12, sm: 4 }}>
            <Results />
          </Grid>
          <Grid
            size={{ xs: 12, sm: 'grow' }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <ErrorBoundary fallback={<CalculatorResultsGraphSkeleton />}>
                <Suspense fallback={<CalculatorResultsGraphSkeleton />}>
                  <CalculatorResultsGraph />
                </Suspense>
              </ErrorBoundary>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </FeatureViewed>
  );
}
